import { PathPickFileRequestKind } from "@/features/pathpick/features/basket/types";
import { PathPickMarketSyncKind } from "@/features/pathpick/types";

// 신청 의도 — 관문을 모두 통과한 뒤 실행할 최종 동작.
// 관문 모달이 저장/확인에 성공하면 이 값을 다시 넘겨 원래 하려던 신청을 이어간다.
// API 연동의 markets — 확인 모달에서 고른 마켓. 아직 고르기 전(신청 버튼 직후)이면 비어 있다.
export type PathPickSyncIntent =
  | { kind: "apiSync"; markets?: PathPickMarketSyncKind[] }
  | { kind: "fileRequest"; formats: PathPickFileRequestKind[] };

// 기본 정보 설정 모달이 어떤 진입 경로로 열렸는지 — 노출 섹션·필수 여부·배너 문구가 이 값 하나로 결정된다.
// standalone은 패널의 [판매자 기본 정보 설정] 버튼으로 연 경우로, 부분 저장을 허용한다.
// guideApi·guideExcel은 시작 가이드의 설정 단계에서 연 경우다 — 판매 방식에 따라 노출 섹션만
// API/엑셀로 갈리고, 둘 다 연습 자리라 신청 관문용 배너와 필수 검사를 빼고 부분 저장만 허용한다.
// 두 값은 신청 플로우 스택에 절대 올라가지 않는다
// (resolveBlockingStep·useSyncRequestFlow는 나머지 셋만 만든다).
export type PathPickBuyerInfoVariant =
  | "standalone"
  | "apiSync"
  | "fileRequest"
  | "guideApi"
  | "guideExcel";

// 플로우의 화면 단계.
export type PathPickSyncFlowStep =
  | { name: "fileFormatSelect" }
  | { name: "buyerInfo"; variant: PathPickBuyerInfoVariant }
  // 선택한 상품이 모두 이미 처리(성공/처리 중)돼 접수할 대상이 없는 종료 단계.
  // API 연동과 파일 신청이 함께 쓰므로 이름에 채널을 담지 않는다.
  | { name: "alreadyRequested" }
  | { name: "syncConfirm" }
  | { name: "syncComplete" }
  | { name: "downloadComplete" };

export type PathPickSyncFlowStepName = PathPickSyncFlowStep["name"];

export interface PathPickSyncFlowState {
  // 진행 중인 신청 의도 — 관문 모달에서 재개할 때 쓴다. 단독 진입이면 null.
  intent: PathPickSyncIntent | null;
  // 마지막 원소가 현재 단계. 빈 배열이면 모달이 모두 닫힌 상태다.
  stack: PathPickSyncFlowStep[];
  // 관문을 다시 시도했는데 여전히 미해결일 때 — 모달을 재마운트하지 않고 인라인 경고만 띄운다.
  blockedStep: PathPickSyncFlowStepName | null;
}

export type PathPickSyncFlowAction =
  // mode — push: 취소 시 이전 단계로 돌아가야 할 때(파일 신청 관문),
  //        replace: 취소 = 플로우 전체 종료일 때(API 연동 관문),
  //        reset: 종착 단계라 되돌아갈 곳이 없을 때(완료 모달 등).
  | {
      type: "GO_TO";
      step: PathPickSyncFlowStep;
      // 생략하면 진행 중인 의도를 그대로 유지한다.
      intent?: PathPickSyncIntent | null;
      mode: "push" | "replace" | "reset";
    }
  | { type: "BLOCK"; step: PathPickSyncFlowStep }
  | { type: "BACK" }
  | { type: "CLOSE" };

export const initialSyncFlowState: PathPickSyncFlowState = {
  intent: null,
  stack: [],
  blockedStep: null,
};

export const getCurrentSyncFlowStep = (
  state: PathPickSyncFlowState
): PathPickSyncFlowStep | null => state.stack[state.stack.length - 1] ?? null;

export const syncFlowReducer = (
  state: PathPickSyncFlowState,
  action: PathPickSyncFlowAction
): PathPickSyncFlowState => {
  switch (action.type) {
    case "GO_TO": {
      const stack =
        action.mode === "push"
          ? [...state.stack, action.step]
          : action.mode === "reset"
          ? [action.step]
          : // replace — 빈 스택에서는 push와 같게 동작한다(플로우 진입).
            [...state.stack.slice(0, -1), action.step];

      return {
        intent: action.intent !== undefined ? action.intent : state.intent,
        stack,
        blockedStep: null,
      };
    }
    // 같은 단계에 머무르되 사유는 최신으로 갱신한다.
    // 단계 이름이 그대로라 모달은 재마운트되지 않고 입력값도 유지된다.
    case "BLOCK":
      return {
        ...state,
        stack: [...state.stack.slice(0, -1), action.step],
        blockedStep: action.step.name,
      };
    case "BACK": {
      const stack = state.stack.slice(0, -1);
      // 되돌아갈 단계가 없으면 플로우가 끝난 것이므로 의도도 함께 버린다.
      return stack.length === 0
        ? initialSyncFlowState
        : { ...state, stack, blockedStep: null };
    }
    case "CLOSE":
      return initialSyncFlowState;
    default:
      return state;
  }
};
